import type { Instrument } from "@trader/shared/MarketData"
import { BigDecimal } from "effect"
import { useMemo, useState } from "react"
import { toast } from "../../ui/toasts.ts"
import { acceptedQty, buttonPrice, startingQty, stepQty, ticketBlockedReason, ticketInstrument } from "./orderTicket.ts"
import { placeChartOrder } from "./positionActions.ts"
import { messageFor } from "./tradingApi.ts"
import { lastTicketQty, useAccountState, useLastPrice, useLastTicketQty } from "./tradingData.ts"

// TradingView's Buy / Sell buttons, on a chart's top-left (docs/design/0013): SELL and BUY place a
// market order at once on the followed paper account; the box between them is the instrument's
// quantity, which the right-click limit and stop orders and the order ticket use too. The paper
// venue has no bid and ask, so both show the chart's last price.

export function QuickTrade({ instrument }: { readonly instrument: Instrument }) {
  const state = useAccountState()
  const last = useLastPrice(instrument.id)
  const spec = useMemo(() => ticketInstrument(instrument), [instrument])
  const qty = startingQty(useLastTicketQty(instrument.id), spec.qtyStep, spec.lotSize)
  const [typing, setTyping] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const account = state?.summary ?? null
  if (state === null || account === null) return null
  const blocked = ticketBlockedReason(account, instrument)
  const price = last === null ? null : buttonPrice(last, spec.pricePrecision)

  const commit = (text: string) => {
    const accepted = acceptedQty(text, spec.qtyStep)
    if (accepted !== null) lastTicketQty.set(instrument.id, accepted)
    else if (text.trim() !== "" && text.trim() !== qty) {
      toast(
        "error",
        `${instrument.symbol} can't trade ${text.trim()}`,
        spec.fractional
          ? `Use a quantity above zero in steps of ${BigDecimal.format(spec.qtyStep)}, e.g. 1.6.`
          : `Use whole shares in lots of ${BigDecimal.format(spec.lotSize)}.`
      )
    }
    setTyping(null)
  }

  const place = (side: "Buy" | "Sell") => {
    if (busy || blocked !== null) return
    const held = state.positions.find((position) => position.instrumentId === instrument.id && position.product === "Cash")?.qty ??
      BigDecimal.fromBigInt(0n)
    setBusy(true)
    placeChartOrder(account, instrument, { side, type: "Market" }, BigDecimal.fromStringUnsafe(qty), null, held)
      .catch((error: unknown) => {
        const message = messageFor(error)
        toast("error", `Couldn't ${side.toLowerCase()}: ${message.title}`, message.detail)
      })
      .finally(() => setBusy(false))
  }

  const side = (name: "Buy" | "Sell") => (
    <button
      type="button"
      className={`quick-trade-side quick-trade-${name.toLowerCase()}`}
      disabled={busy || blocked !== null}
      title={blocked ?? `${name} ${qty} ${instrument.symbol} at market`}
      aria-label={`${name} ${qty} at market`}
      onClick={() => place(name)}
    >
      <span className="quick-trade-name">{name.toUpperCase()}</span>
      <span className="quick-trade-price">
        {price === null ? "—" : (
          <>
            <small>{price.small}</small>
            <strong>{price.big}</strong>
          </>
        )}
      </span>
    </button>
  )

  return (
    // Its own clicks and drags stay off the chart under it.
    <div className="quick-trade" onPointerDown={(event) => event.stopPropagation()} onContextMenu={(event) => event.stopPropagation()}>
      {side("Sell")}
      <div className="quick-trade-qty">
        <button type="button" aria-label="Less" onClick={() => lastTicketQty.set(instrument.id, stepQty(qty, spec.qtyNudge, -1))}>−</button>
        <input
          type="text"
          inputMode={spec.fractional ? "decimal" : "numeric"}
          aria-label="Quantity"
          value={typing ?? qty}
          onChange={(event) => setTyping(event.target.value)}
          onBlur={(event) => commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit(event.currentTarget.value)
            if (event.key === "Escape") setTyping(null)
          }}
        />
        <button type="button" aria-label="More" onClick={() => lastTicketQty.set(instrument.id, stepQty(qty, spec.qtyNudge, 1))}>+</button>
      </div>
      {side("Buy")}
    </div>
  )
}
